import { useMemo, useState } from 'react';
import MovieCard from './MovieCard';
import { movieList } from '@/mocks/movies';

const filters = ['全部', '科幻', '动作', '剧情', '悬疑', '历史', '动画', '喜剧', '爱情', '冒险', '战争'];

type LatestSectionProps = {
  query: string;
  onQueryChange: (value: string) => void;
};

export default function LatestSection({ query, onQueryChange }: LatestSectionProps) {
  const [genre, setGenre] = useState('全部');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return movieList.filter((m) => {
      const matchGenre = genre === '全部' || m.genres.includes(genre);
      if (!matchGenre) return false;
      if (!q) return true;
      return (
        m.title.toLowerCase().includes(q) ||
        m.originalTitle.toLowerCase().includes(q) ||
        m.genres.some((g) => g.toLowerCase().includes(q)) ||
        m.region.toLowerCase().includes(q) ||
        String(m.year).includes(q)
      );
    });
  }, [query, genre]);

  return (
    <section id="latest" className="scroll-mt-20 bg-background-50">
      <div className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-primary-600 uppercase">
              <i className="ri-movie-2-line"></i> NOW SHOWING
            </span>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">最新上线影片</h2>
            <p className="mt-2 text-sm text-foreground-600 max-w-2xl">
              每日更新的高清资源列表，覆盖 2025 年院线新片与近年高分佳作。可按题材筛选，或直接搜索片名快速定位。
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-background-300 bg-background-50 p-2 md:w-72">
            <i className="ri-search-line text-foreground-400 text-lg"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="搜索片名 / 题材 / 地区"
              aria-label="在本页筛选影片"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 py-1.5 focus:outline-none"
            />
            {query && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => onQueryChange('')}
                className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setGenre(f)}
              className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors cursor-pointer whitespace-nowrap ${
                genre === f
                  ? 'bg-primary-500 text-background-50 border-primary-500'
                  : 'bg-background-50 text-foreground-600 border-background-300 hover:border-primary-300 hover:text-primary-700'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <p className="mt-4 text-xs text-foreground-500">
          共找到 <strong className="text-primary-700">{filtered.length}</strong> 部影片
          {genre !== '全部' && <> · 题材「{genre}」</>}
          {query.trim() && <> · 关键词「{query.trim()}」</>}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
            {filtered.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="mt-10 py-16 flex flex-col items-center justify-center text-center border border-dashed border-background-300 rounded-lg">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            <p className="mt-3 text-sm text-foreground-700">没有找到匹配的影片</p>
            <p className="mt-1 text-xs text-foreground-500">试试更换关键词，或点击「全部」查看所有资源。</p>
            <button
              type="button"
              onClick={() => {
                onQueryChange('');
                setGenre('全部');
              }}
              className="mt-5 px-5 py-2 rounded-md bg-secondary-500 text-background-50 text-sm hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}